import React, { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "@/lib/auth";
import { enrolInfo, enrolLine } from "@/lib/enrol";
import { ArrowLeft } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

export const PageHeader = ({ eyebrow, title, sub, back = false, backTo = "/", backLabel = "Back to home" }) => (
  <section className="pt-32 pb-16 px-6 border-b border-white/10">
    <div className="max-w-[1400px] mx-auto">
      {back && (
        <Link to={backTo} data-testid="page-back-link" className="inline-flex items-center gap-2 text-zinc-400 hover:text-red-500 text-xs uppercase tracking-widest mb-6">
          <ArrowLeft size={14}/>{backLabel}
        </Link>
      )}
      {eyebrow && <div className="eyebrow mb-4">{eyebrow}</div>}
      <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">{title}</h1>
      {sub && <p className="text-zinc-400 mt-6 max-w-2xl">{sub}</p>}
    </div>
  </section>
);

export default function Classes() {
  const [sp] = useSearchParams();
  const trialMode = sp.get("trial") === "1";
  const { settings } = useOutletContext();
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  useEffect(() => {
    api.get("/batches").then(r => setBatches(r.data));
    api.get("/sessions").then(r => setSessions(r.data || []));
    api.get("/terms").then(r => setTerms(r.data || []));
  }, []);

  return (
    <>
      <PageHeader eyebrow={trialMode ? "Free trial" : "Programs"} title={trialMode ? "Pick a class to try free" : "Classes"} sub={trialMode ? "Choose the age group, then the day that suits — your first class is on us, no payment needed." : "Age-grouped batches — from Sub Juniors to Adults and our competition track."} />
      {settings?.classes_intro && (
        <section className="max-w-[900px] mx-auto px-4 sm:px-6 pt-12 pb-4" data-testid="classes-intro">
          <div className="prose prose-invert prose-lg max-w-none leading-relaxed [&_*]:!text-inherit" style={{ color: settings?.classes_intro_color || "#e4e4e7" }} dangerouslySetInnerHTML={{ __html: sanitizeHtml((settings.classes_intro || "").replace(/color\s*:\s*[^;"']+;?/gi, "").replace(/font-family\s*:\s*[^;"']+;?/gi, "").replace(/background(?:-color)?\s*:\s*[^;"']+;?/gi, "")) }} />
        </section>
      )}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="batches-grid">
        {batches.map((b) => (
          <Link key={b.id} to={`/classes/${b.id}${trialMode ? "?trial=1" : ""}`} className="group relative aspect-[4/5] overflow-hidden card-surface card-hover" data-testid={`batch-link-${b.name.toLowerCase().replace(/\s/g,'-')}`}>
            <img src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6">
              <div className="eyebrow text-white/70 mb-2">{b.age_range}</div>
              <h3 className="font-display text-4xl uppercase">{b.name}</h3>
              <p className="text-zinc-300 text-sm mt-3 line-clamp-2">{b.description}</p>
              {enrolInfo(b.id, sessions, terms) && <p className="text-red-400 text-[11px] uppercase tracking-widest mt-3" data-testid={`classes-term-${b.id}`}>{enrolLine(enrolInfo(b.id, sessions, terms))}</p>}
            </div>
          </Link>
        ))}
        {batches.length === 0 && <div className="col-span-full text-zinc-500 text-center py-20" data-testid="classes-empty">No upcoming classes.</div>}
      </div>
    </>
  );
}
